import * as React from 'react'
import { BaseComponent } from '../modelling/components/base_component'
import {
  ComponentInfoEntry,
  ComponentSocketInfo,
  ComponentSourceInfo,
  ComponentVariantList,
  SOCKET_COLOR_LABELS,
  deriveOutputsForComponent,
  deriveSelectableSourcesForComponent,
  getComponentInfo,
  parseUnitMarker,
  socketPrimaryText,
  socketSecondaryText,
} from '../modelling/component_info'
import { ProjectPermissions } from './project_editor'
import { isRegionAvailable } from "../utils/region_permissions"
import { AgentActions } from './model_view_agent'
import {
  AegisChatBody,
  AegisChatHistory,
  AegisChatToolbar,
  AegisSessionBoundary,
  useAegisSession,
} from '../aegis/session'
import { highlightSearchMatch } from './search_highlight'

/**
 * Right-hand sidebar attached to the model editor — hosts the Toolbox
 * (draggable component palette + sticky-note quick-add) plus the AEGIS
 * agent chat tab (whose runtime + panel live in `model_view_agent`). The
 * canvas in `model_view` interprets the `dataTransfer` payload from these
 * drags to create nodes/notes at the drop position.
 */

export type ModelSidebarTab = "agent" | "toolbox"

const TOOLBOX_CATEGORY_ACCENTS: Record<string, { icon: string; color: string }> = {
  Inputs: { icon: "fa-database", color: "#4f8df9" },
  Outputs: { icon: "fa-arrow-up-from-bracket", color: "#4cc38a" },
  Conversions: { icon: "fa-arrows-rotate", color: "#e0a64a" },
  "Set operations": { icon: "fa-shapes", color: "#7e57f0" },
  Arithmetic: { icon: "fa-calculator", color: "#46c4d6" },
  Calculations: { icon: "fa-square-root-variable", color: "#d36ad1" },
  Indonesia: { icon: "fa-earth-asia", color: "#f08a4b" },
  Kalimantan: { icon: "fa-tree", color: "#2e8b57" },
  Gorontalo: { icon: "fa-mountain-sun", color: "#f0a85b" },
  Global: { icon: "fa-globe", color: "#7ec8ff" },
}

const TOOLBOX_DEFAULT_ACCENT = { icon: "fa-cube", color: "#9aa3ad" }

function getCategoryAccent(category: string) {
  return TOOLBOX_CATEGORY_ACCENTS[category] ?? TOOLBOX_DEFAULT_ACCENT
}

function componentDisplayName(component: BaseComponent): string {
  return component.contextMenuName?.trim() || component.name
}

/**
 * Build the lowercase haystack used by the Toolbox search box. We match
 * against:
 *   • the visible (context-menu) name and the internal registration name
 *   • the category title ("Inputs", "Indonesia", …)
 *   • the publisher names (single `sourceLabel` or every entry's `label` in
 *     a multi-source list) and per-source notes — so e.g. typing "Defra"
 *     surfaces Digital Model even though that word never appears in the
 *     visible component name
 *   • the taxonomy tags (LULC, Elevation, Biodiversity, …) so users can
 *     filter the palette by data type rather than memorising names
 *
 * Returned as a single space-joined lowercase string for cheap `includes()`
 * checks.
 */
function buildSearchHaystack(
  component: BaseComponent,
  display: string,
  category: string
): string {
  const parts: string[] = [display, component.name, category]
  const info = getComponentInfo(component.name)
  if (info) {
    if (info.sourceLabel) parts.push(info.sourceLabel)
    if (info.sources) {
      for (const src of info.sources) {
        parts.push(src.label)
        if (src.note) parts.push(src.note)
        if (src.region) parts.push(src.region)
      }
    }
    if (info.tags) parts.push(...info.tags)
  }
  return parts.join(" ").toLowerCase()
}

/**
 * Replaces the browser's default ~50% opacity drag ghost with a cloned,
 * fully-opaque snapshot of the source card, then schedules the source to
 * flip into its "in transit" look once the snapshot has been taken.
 *
 * Returns nothing — mutates the event's `dataTransfer` and `currentTarget`.
 * Safe to call even when `dataTransfer` is null; in that case it's a no-op.
 */
function setupOpaqueDragPreview(event: React.DragEvent<HTMLButtonElement>) {
  if (!event.dataTransfer) return

  const target = event.currentTarget
  const rect = target.getBoundingClientRect()
  const clone = target.cloneNode(true) as HTMLElement
  clone.classList.add("model-editor-toolbox-tool--drag-image")
  clone.style.width = `${rect.width}px`
  clone.style.height = `${rect.height}px`
  document.body.appendChild(clone)
  const offsetX = event.clientX - rect.left
  const offsetY = event.clientY - rect.top
  event.dataTransfer.setDragImage(clone, offsetX, offsetY)
  // Clone only needs to live long enough for the browser to snapshot it.
  window.setTimeout(() => clone.remove(), 0)

  // Defer the --dragging class so the browser captures an un-styled snapshot
  // first (otherwise the native ghost would also be scaled / dimmed).
  window.requestAnimationFrame(() => {
    target.classList.add("model-editor-toolbox-tool--dragging")
  })
}

function clearDraggingState(event: React.DragEvent<HTMLButtonElement>) {
  event.currentTarget.classList.remove("model-editor-toolbox-tool--dragging")
}

export interface ModelAgentSidebarProps {
  /** Components available in the editor; grouped by `category` into draggable tool cards. */
  components: BaseComponent[]
  /**
   * Project's region / feature permissions. Used by the info panel to hide
   * dataset sources that don't apply to the active project — e.g. a UK-only
   * project doesn't see Indonesia / ASTER GDEM rows inside the Digital Model
   * card.
   */
  permissions?: ProjectPermissions
  /**
   * Active team id — substituted into `{teamId}` placeholders in component
   * descriptions to produce deep links to the team admin pages
   * (Expressions, Extents, Paintbrush). Optional so the sidebar still renders
   * sensibly in tests / Storybook fixtures with no team context, in which
   * case the placeholder text is left as-is and the link will 404.
   */
  teamId?: number
  /** Project currently open in the editor — required to persist AEGIS chat logs. */
  projectId?: number
  /**
   * Whether the active team has a Groq API key configured. When false, the
   * chat composer is disabled with a hint pointing at team settings — the
   * key itself is never sent to the browser, only this boolean.
   */
  groqConfigured?: boolean
  /** Whether the team's selected AEGIS provider has an API key configured. */
  agentConfigured?: boolean
  /** Human-readable label for the team's effective AEGIS provider. */
  agentProvider?: string
  /** Human-readable label for the team's effective AEGIS model. */
  agentModel?: string
  /** Raw provider id ("groq" | "anthropic" | "deepseek") used to route the agent API. */
  agentProviderId?: string
  /** Raw model id sent to the provider API (e.g. "claude-sonnet-4-6"). */
  agentModelId?: string
  /**
   * Optional editor action bridge — when provided, the AEGIS chat tab can
   * mutate the graph (add / remove nodes) on the user's behalf. Omitting
   * this prop simply degrades the agent to a chat-only assistant.
   */
  actions?: AgentActions
  /**
   * When false (model view is CSS-hidden), skip mounting the chat panel so
   * the map-view AEGIS tab can own the same session without duplicate DOM.
   */
  visible?: boolean
}

/**
 * Right-hand sidebar attached to the model editor — hosts the Toolbox
 * (draggable component palette + sticky-note quick-add) plus the AEGIS
 * agent chat tab (whose runtime + panel live in `model_view_agent`). The
 * canvas in `model_view` interprets the `dataTransfer` payload from these
 * drags to create nodes/notes at the drop position.
 */

export function ModelAgentSidebar(props: ModelAgentSidebarProps) {
  return (
    <AegisSessionBoundary
      teamId={props.teamId}
      projectId={props.projectId}
      actions={props.actions}
    >
      <ModelAgentSidebarInner {...props} />
    </AegisSessionBoundary>
  )
}

function ModelAgentSidebarInner({ components, permissions, teamId, visible = true }: ModelAgentSidebarProps) {
  const [open, setOpen] = React.useState(true)
  const [toolboxSearch, setToolboxSearch] = React.useState("")
  const { activeSessionId, aegisTabOpen, setAegisTabOpen, isSending } = useAegisSession()
  const activeTab: ModelSidebarTab = aegisTabOpen ? "agent" : "toolbox"
  const showAgentChat = visible && activeTab === "agent"

  const toolboxGroups = React.useMemo(() => {
    const query = toolboxSearch.trim().toLowerCase()
    const groups = new Map<string, BaseComponent[]>()
    for (const component of components) {
      const display = componentDisplayName(component)
      const category = component.category || "Other"
      if (query) {
        if (!buildSearchHaystack(component, display, category).includes(query)) {
          continue
        }
      }
      const list = groups.get(category) ?? []
      list.push(component)
      groups.set(category, list)
    }
    return Array.from(groups.entries()).map(([category, items]) => ({
      category,
      items: items.slice().sort((a, b) =>
        componentDisplayName(a).localeCompare(componentDisplayName(b))
      ),
    }))
  }, [components, toolboxSearch])

  const toolboxTotal = toolboxGroups.reduce((sum, group) => sum + group.items.length, 0)

  if (!open) {
    return (
      <button
        type="button"
        className="model-editor-chat-reopen"
        onClick={() => setOpen(true)}
        aria-label="Open sidebar"
      >
        <i className="fas fa-toolbox" />
        <span className="model-editor-chat-reopen-label">Sidebar</span>
      </button>
    )
  }

  return (
    <aside className="model-editor-chat" aria-label="Model editor sidebar">
      <div className="model-editor-chat-top">
        <header className="model-editor-chat-header">
          <span className="model-editor-chat-avatar" aria-hidden>
            <i className={`fas ${activeTab === "agent" ? "fa-comments" : "fa-toolbox"}`} />
          </span>
          <div className="model-editor-chat-heading">
            <span className="model-editor-chat-title">
              {activeTab === "agent" ? "AEGIS" : "Toolbox"}
            </span>
            {showAgentChat && activeSessionId != null && (
              <span className="model-editor-chat-subtitle">Continuing earlier chat</span>
            )}
          </div>
          {showAgentChat && <AegisChatToolbar />}
          <button
            type="button"
            className="model-editor-chat-icon-btn"
            onClick={() => setOpen(false)}
            aria-label="Hide sidebar"
          >
            <i className="fas fa-chevron-right" />
          </button>
        </header>

        {showAgentChat && <AegisChatHistory />}
      </div>

      <nav className="model-editor-chat-tabs" role="tablist" aria-label="Sidebar sections">
        <button
          type="button"
          role="tab"
          aria-selected={activeTab === "toolbox"}
          className={`model-editor-chat-tab${activeTab === "toolbox" ? " model-editor-chat-tab--active" : ""}`}
          onClick={() => setAegisTabOpen(false)}
        >
          <i className="fas fa-toolbox" aria-hidden />
          <span>Toolbox</span>
        </button>
        <button
          type="button"
          role="tab"
          aria-selected={activeTab === "agent"}
          className={`model-editor-chat-tab${activeTab === "agent" ? " model-editor-chat-tab--active" : ""}`}
          onClick={() => setAegisTabOpen(true)}
          aria-busy={isSending}
          title={isSending ? "AEGIS is running" : undefined}
        >
          <i className={`fas ${isSending ? "fa-spinner fa-spin" : "fa-comments"}`} aria-hidden />
          <span>AEGIS</span>
        </button>
      </nav>

      {showAgentChat && <AegisChatBody />}

      {activeTab === "toolbox" && (
        <ToolboxPanel
          components={components}
          toolboxSearch={toolboxSearch}
          setToolboxSearch={setToolboxSearch}
          toolboxGroups={toolboxGroups}
          toolboxTotal={toolboxTotal}
          permissions={permissions}
          teamId={teamId}
        />
      )}
    </aside>
  )
}

// ----- Toolbox panel ---------------------------------------------------------

interface ToolboxGroup {
  category: string
  items: BaseComponent[]
}

interface ToolboxPanelProps {
  components: BaseComponent[]
  toolboxSearch: string
  setToolboxSearch: (value: string) => void
  toolboxGroups: ToolboxGroup[]
  toolboxTotal: number
  permissions?: ProjectPermissions
  teamId?: number
}

function ToolboxPanel({
  components,
  toolboxSearch,
  setToolboxSearch,
  toolboxGroups,
  toolboxTotal,
  permissions,
  teamId,
}: ToolboxPanelProps) {
  /**
   * Set of category names the user has explicitly expanded. Empty by default
   * — so all categories load collapsed. While a search is active, every
   * group that has matches is force-expanded regardless of this set, so the
   * user can see results without first clicking each header.
   */
  const [expandedCategories, setExpandedCategories] = React.useState<Set<string>>(
    () => new Set<string>()
  )
  /** Component names whose description dropdown is currently open. */
  const [expandedComponents, setExpandedComponents] = React.useState<Set<string>>(
    () => new Set<string>()
  )

  const isSearching = toolboxSearch.trim().length > 0

  const toggleCategory = React.useCallback((category: string) => {
    setExpandedCategories(prev => {
      const next = new Set(prev)
      if (next.has(category)) next.delete(category)
      else next.add(category)
      return next
    })
  }, [])

  const toggleComponent = React.useCallback((componentName: string) => {
    setExpandedComponents(prev => {
      const next = new Set(prev)
      if (next.has(componentName)) next.delete(componentName)
      else next.add(componentName)
      return next
    })
  }, [])

  const allExpanded =
    toolboxGroups.length > 0 &&
    toolboxGroups.every(group => expandedCategories.has(group.category))

  const setAllExpanded = (expanded: boolean) => {
    if (expanded) {
      setExpandedCategories(new Set(toolboxGroups.map(g => g.category)))
    } else {
      setExpandedCategories(new Set())
    }
  }

  return (
    <div className="model-editor-toolbox">
      <div className="model-editor-toolbox-search">
        <i className="fas fa-magnifying-glass" aria-hidden />
        <input
          type="search"
          placeholder="Search components…"
          aria-label="Search components"
          value={toolboxSearch}
          onChange={event => setToolboxSearch(event.target.value)}
        />
        {toolboxSearch && (
          <button
            type="button"
            className="model-editor-toolbox-search-clear"
            onClick={() => setToolboxSearch("")}
            aria-label="Clear search"
            title="Clear search"
          >
            <i className="fas fa-xmark" />
          </button>
        )}
      </div>

      {/* Quick-add row — items here aren't components, but draggable
          "primitives" the canvas knows how to interpret. Lives above the
          search summary so it's always visible regardless of filter. */}
      <div className="model-editor-toolbox-quick">
        <NoteQuickAddTool />
      </div>

      <div className="model-editor-toolbox-summary">
        <span>
          {components.length === 0
            ? "Components load with the editor…"
            : toolboxSearch
              ? `${toolboxTotal} match${toolboxTotal === 1 ? "" : "es"}`
              : `${components.length} components`}
        </span>
        {toolboxGroups.length > 0 && !isSearching && (
          <button
            type="button"
            className="model-editor-toolbox-summary-action"
            onClick={() => setAllExpanded(!allExpanded)}
            aria-label={allExpanded ? "Collapse all categories" : "Expand all categories"}
            title={allExpanded ? "Collapse all" : "Expand all"}
          >
            {allExpanded ? "Collapse all" : "Expand all"}
          </button>
        )}
      </div>
      <div className="model-editor-toolbox-groups">
        {toolboxGroups.length === 0 && components.length > 0 && (
          <div className="model-editor-toolbox-empty">
            <i className="fas fa-circle-info" aria-hidden />
            <span>No components match “{toolboxSearch}”.</span>
          </div>
        )}
        {toolboxGroups.map(group => (
          <ToolboxGroupSection
            key={group.category}
            group={group}
            expanded={isSearching || expandedCategories.has(group.category)}
            onToggle={() => toggleCategory(group.category)}
            expandedComponents={expandedComponents}
            onToggleComponent={toggleComponent}
            permissions={permissions}
            teamId={teamId}
            toolboxSearch={toolboxSearch}
          />
        ))}
      </div>
    </div>
  )
}

interface ToolboxGroupSectionProps {
  group: ToolboxGroup
  expanded: boolean
  onToggle: () => void
  expandedComponents: Set<string>
  onToggleComponent: (componentName: string) => void
  permissions?: ProjectPermissions
  teamId?: number
  toolboxSearch: string
}

function ToolboxGroupSection({
  group,
  expanded,
  onToggle,
  expandedComponents,
  onToggleComponent,
  permissions,
  teamId,
  toolboxSearch,
}: ToolboxGroupSectionProps) {
  const accent = getCategoryAccent(group.category)
  return (
    <section
      className={`model-editor-toolbox-group${expanded ? " model-editor-toolbox-group--expanded" : " model-editor-toolbox-group--collapsed"}`}
    >
      <h4 className="model-editor-toolbox-group-title">
        <button
          type="button"
          className="model-editor-toolbox-group-toggle"
          aria-expanded={expanded}
          aria-controls={`toolbox-group-${slugify(group.category)}`}
          onClick={onToggle}
        >
          <i
            className={`fas fa-chevron-right model-editor-toolbox-group-chevron${expanded ? " model-editor-toolbox-group-chevron--open" : ""}`}
            aria-hidden
          />
          <span
            className="model-editor-toolbox-group-badge"
            style={{ background: `${accent.color}22`, color: accent.color }}
            aria-hidden
          >
            <i className={`fas ${accent.icon}`} />
          </span>
          <span className="model-editor-toolbox-group-name">
            {toolboxSearch.trim()
              ? highlightSearchMatch(group.category, toolboxSearch)
              : group.category}
          </span>
          <span className="model-editor-toolbox-group-count">{group.items.length}</span>
        </button>
      </h4>
      {expanded && (
        <div
          id={`toolbox-group-${slugify(group.category)}`}
          className="model-editor-toolbox-tools"
        >
          {group.items.map(component => (
            <ComponentTool
              key={component.name}
              component={component}
              accent={accent}
              expanded={expandedComponents.has(component.name)}
              onToggle={() => onToggleComponent(component.name)}
              permissions={permissions}
              teamId={teamId}
              toolboxSearch={toolboxSearch}
            />
          ))}
        </div>
      )}
    </section>
  )
}

/** Lowercase, alphanumeric-and-dash slug for aria-controls ids. */
function slugify(value: string): string {
  return value
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/(^-|-$)/g, "")
}

interface ComponentToolProps {
  component: BaseComponent
  accent: { icon: string; color: string }
  expanded: boolean
  onToggle: () => void
  permissions?: ProjectPermissions
  teamId?: number
  toolboxSearch: string
}

function ComponentTool({ component, accent, expanded, onToggle, permissions, teamId, toolboxSearch }: ComponentToolProps) {
  const display = componentDisplayName(component)
  const info = getComponentInfo(component.name)
  /**
   * Description sources in order of preference:
   *   1. The curated `component_info.ts` entry — this is where elaborated
   *      sidebar copy lives.
   *   2. Whatever the component itself set as `BaseComponent.description`
   *      (none of them do at time of writing, but the hook is kept open).
   */
  const description =
    info?.description?.trim() ||
    (component as BaseComponent).description?.trim() ||
    ""
  const descriptionId = `toolbox-tool-desc-${slugify(component.name)}`
  return (
    <div
      className={`model-editor-toolbox-tool-row${expanded ? " model-editor-toolbox-tool-row--expanded" : ""}`}
    >
      <button
        type="button"
        className="model-editor-toolbox-tool"
        draggable
        aria-expanded={expanded}
        aria-controls={descriptionId}
        onClick={onToggle}
        onDragStart={event => {
          if (!event.dataTransfer) return
          event.dataTransfer.effectAllowed = "copy"
          event.dataTransfer.setData(
            "application/x-landscapes-component",
            component.name
          )
          event.dataTransfer.setData("text/plain", display)
          setupOpaqueDragPreview(event)
        }}
        onDragEnd={clearDraggingState}
      >
        <span
          className="model-editor-toolbox-tool-icon"
          style={{ background: `${accent.color}22`, color: accent.color }}
          aria-hidden
        >
          <i className={`fas ${accent.icon}`} />
        </span>
        <span className="model-editor-toolbox-tool-text">
          <span className="model-editor-toolbox-tool-name">
            {toolboxSearch.trim()
              ? highlightSearchMatch(display, toolboxSearch)
              : display}
          </span>
          {display !== component.name && (
            <span className="model-editor-toolbox-tool-desc">
              {toolboxSearch.trim()
                ? highlightSearchMatch(component.name, toolboxSearch)
                : component.name}
            </span>
          )}
        </span>
        <i
          className={`fas fa-chevron-down model-editor-toolbox-tool-chevron${expanded ? " model-editor-toolbox-tool-chevron--open" : ""}`}
          aria-hidden
        />
      </button>
      {expanded && (
        <div
          id={descriptionId}
          className="model-editor-toolbox-tool-description"
          role="region"
          aria-label={`${display} details`}
        >
          <ComponentInfoPanel
            description={description}
            info={info}
            permissions={permissions}
            component={component}
            teamId={teamId}
          />
        </div>
      )}
    </div>
  )
}

// ----- Component info panel --------------------------------------------------

interface ComponentInfoPanelProps {
  description: string
  info?: ComponentInfoEntry
  permissions?: ProjectPermissions
  component: BaseComponent
  teamId?: number
}

/**
 * Replace `{teamId}` (and other team-scoped tokens, if we add any later)
 * inside curated description copy with the active project's team id. Falls
 * back to leaving the placeholder verbatim when no team id is available —
 * better than rendering a hard-coded `0` that would 404 when followed.
 */
function substituteDescriptionTokens(description: string, teamId: number | undefined): string {
  if (teamId === undefined || teamId === null) return description
  return description.replace(/\{teamId\}/g, String(teamId))
}

/**
 * Renders a description string that may contain inline markdown-style links
 * — `[text](url)` — as React nodes. Anything outside a link match is kept
 * as plain text. We intentionally support only the link form (no bold,
 * italics, lists, code spans, etc.) because the component info panel is
 * rendered in a constrained UI surface and the broader markdown surface
 * would invite typo-bugs we'd then have to maintain.
 *
 * Links open in a new tab so the user doesn't lose unsaved model state, and
 * carry `noopener noreferrer` to avoid leaking the model editor window
 * reference.
 */
function renderMarkdownLinks(text: string): React.ReactNode {
  const parts: React.ReactNode[] = []
  let lastIndex = 0
  // Non-greedy on both halves so two adjacent links on one line still parse
  // as two distinct matches rather than one over-long span.
  const regex = /\[([^\]]+)\]\(([^)\s]+)\)/g
  let match: RegExpExecArray | null

  while ((match = regex.exec(text)) !== null) {
    const [, linkText, url] = match
    if (match.index > lastIndex) {
      parts.push(text.substring(lastIndex, match.index))
    }
    parts.push(
      <a
        key={`md-link-${match.index}`}
        href={url}
        target="_blank"
        rel="noopener noreferrer"
        className="model-editor-toolbox-tool-description-link"
      >
        {linkText}
        <i
          className="fas fa-arrow-up-right-from-square model-editor-toolbox-tool-source-icon"
          aria-hidden
        />
      </a>
    )
    lastIndex = regex.lastIndex
  }

  if (lastIndex < text.length) {
    parts.push(text.substring(lastIndex))
  }

  return parts.length > 0 ? <>{parts}</> : text
}

/**
 * Renders the expanded card body: source attribution + URL, taxonomy tags, a
 * dot-coloured input/output socket summary, and the long-form description.
 * Each section is optional — the panel quietly elides anything it doesn't
 * have data for, so unmapped components still get a clean description-only
 * view (matching the previous behaviour).
 *
 * Outputs are sourced in this order of preference:
 *   1. Live introspection of the component instance via
 *      `deriveOutputsForComponent` — this surfaces the actual class /
 *      option list (e.g. MapBiomas's 12 land-cover classes) rather than
 *      the curated "per class" placeholder.
 *   2. The static `outputs` array on the curated `ComponentInfoEntry`.
 */
function ComponentInfoPanel({ description, info, permissions, component, teamId }: ComponentInfoPanelProps) {
  const resolvedDescription = React.useMemo(
    () => substituteDescriptionTokens(description, teamId),
    [description, teamId]
  )
  const derivedOutputs = React.useMemo(
    () => deriveOutputsForComponent(component),
    [component]
  )
  const variantList = React.useMemo(() => {
    const raw = deriveSelectableSourcesForComponent(component)
    if (!raw) return undefined
    if (!raw.itemRegions) return raw
    // Filter region-tagged variants by project permissions (mirrors what the
    // component itself does — e.g. Digital Model's modelList is already
    // permission-filtered, but cheap to be defensive).
    const filtered: ComponentVariantList = {
      label: raw.label,
      items: [],
      itemRegions: [],
      itemBadges: raw.itemBadges ? [] : undefined,
    }
    raw.items.forEach((item, idx) => {
      const region = raw.itemRegions?.[idx]
      if (isRegionAvailable(region, permissions)) {
        filtered.items.push(item)
        filtered.itemRegions!.push(region)
        if (filtered.itemBadges) {
          filtered.itemBadges.push(raw.itemBadges?.[idx])
        }
      }
    })
    return filtered.items.length > 0 ? filtered : undefined
  }, [component, permissions])

  const outputs = derivedOutputs && derivedOutputs.length > 0 ? derivedOutputs : info?.outputs
  const inputs = info?.inputs

  const hasInputs = !!inputs && inputs.length > 0
  const hasOutputs = !!outputs && outputs.length > 0
  const hasSockets = hasInputs || hasOutputs
  const hasVariants = !!variantList && variantList.items.length > 0

  /**
   * Hide any multi-source rows whose `region` is not enabled for the active
   * project (mirrors DigitalModelComponent's own filter). A project that's
   * UK-only therefore won't see Indonesia rows on the Digital Model card,
   * etc. Sources with no `region` set, or set to "Global", are always kept.
   */
  const visibleSources = React.useMemo(() => {
    if (!info?.sources) return []
    return info.sources.filter(src => isRegionAvailable(src.region, permissions))
  }, [info?.sources, permissions])

  const hasMultiSource = visibleSources.length > 0
  const hasSingleSource = !hasMultiSource && (!!info?.sourceLabel || !!info?.sourceUrl)
  const hasSource = hasMultiSource || hasSingleSource
  const hasTags = !!info?.tags && info.tags.length > 0

  return (
    <>
      {hasMultiSource && <SourceList sources={visibleSources} />}
      {hasSingleSource && (
        <SingleSource label={info!.sourceLabel} url={info!.sourceUrl} />
      )}

      {hasTags && (
        <div className="model-editor-toolbox-tool-tags" aria-label="Data tags">
          {info!.tags!.map(tag => (
            <span key={tag} className="model-editor-toolbox-tool-tag">
              {tag}
            </span>
          ))}
        </div>
      )}

      {hasVariants && <VariantList variants={variantList!} />}

      {hasSockets && (
        <div className="model-editor-toolbox-tool-sockets">
          {hasInputs && <SocketList title="Inputs" sockets={inputs!} />}
          {hasOutputs && <SocketList title="Outputs" sockets={outputs!} />}
        </div>
      )}

      {resolvedDescription ? (
        <p className="model-editor-toolbox-tool-description-text">
          {renderMarkdownLinks(resolvedDescription)}
        </p>
      ) : (
        !hasSockets &&
        !hasSource &&
        !hasTags &&
        !hasVariants && (
          <p className="model-editor-toolbox-tool-description--empty">
            No description available yet. Drag onto the canvas to use this component.
          </p>
        )
      )}
    </>
  )
}

/**
 * Compact list of dropdown variants exposed by a component (e.g. MapBiomas
 * "Year" picker, Digital Model "Model" picker). Renders inline pills so a
 * long list (23 MapBiomas years) wraps naturally.
 */
function VariantList({ variants }: { variants: ComponentVariantList }) {
  return (
    <div
      className="model-editor-toolbox-tool-variants"
      aria-label={`Available ${variants.label.toLowerCase()} options`}
    >
      <h6 className="model-editor-toolbox-tool-variants-title">{variants.label}</h6>
      <div className="model-editor-toolbox-tool-variants-items">
        {variants.items.map((item, idx) => (
          <span key={`${item}-${idx}`} className="model-editor-toolbox-tool-variant">
            {variants.itemBadges?.[idx] && (
              <span className="model-editor-toolbox-tool-variant-badge">
                {variants.itemBadges[idx]}
              </span>
            )}
            {item}
          </span>
        ))}
      </div>
    </div>
  )
}

/**
 * One-line source attribution for the common case of a single dataset
 * publisher. Renders as a small link icon + label, with the URL opening in a
 * new tab when present.
 */
function SingleSource({ label, url }: { label?: string; url?: string }) {
  return (
    <div className="model-editor-toolbox-tool-source">
      <i className="fas fa-link" aria-hidden />
      {url ? (
        <a href={url} target="_blank" rel="noopener noreferrer" title={url}>
          {label ?? url}
          <i
            className="fas fa-arrow-up-right-from-square model-editor-toolbox-tool-source-icon"
            aria-hidden
          />
        </a>
      ) : (
        <span>{label}</span>
      )}
    </div>
  )
}

/**
 * Multi-source list for components whose output is drawn from several
 * datasets (e.g. Digital Model = Defra LIDAR + ETH canopy + ASTER GDEM).
 * Each entry can carry an optional region badge and a clarifying note.
 */
function SourceList({ sources }: { sources: ComponentSourceInfo[] }) {
  return (
    <div className="model-editor-toolbox-tool-source-list" aria-label="Data sources">
      <h6 className="model-editor-toolbox-tool-source-list-title">Data sources</h6>
      <ul>
        {sources.map((src, idx) => (
          <li key={`${src.label}-${idx}`} className="model-editor-toolbox-tool-source-row">
            <div className="model-editor-toolbox-tool-source-line">
              <i className="fas fa-link" aria-hidden />
              {src.url ? (
                <a
                  href={src.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  title={src.url}
                >
                  {src.label}
                  <i
                    className="fas fa-arrow-up-right-from-square model-editor-toolbox-tool-source-icon"
                    aria-hidden
                  />
                </a>
              ) : (
                <span>{src.label}</span>
              )}
              {src.region && (
                <span
                  className={`model-editor-toolbox-tool-source-region model-editor-toolbox-tool-source-region--${slugify(src.region)}`}
                >
                  {src.region}
                </span>
              )}
            </div>
            {src.note && (
              <div className="model-editor-toolbox-tool-source-note">{src.note}</div>
            )}
          </li>
        ))}
      </ul>
    </div>
  )
}

function SocketList({ title, sockets }: { title: string; sockets: ComponentSocketInfo[] }) {
  return (
    <div className="model-editor-toolbox-tool-socket-list">
      <h6 className="model-editor-toolbox-tool-socket-title">{title}</h6>
      <ul>
        {sockets.map((socket, idx) => {
          const rawPrimary = socketPrimaryText(socket)
          const rawSecondary = socketSecondaryText(socket)
          // Determine the unit badge:
          //   1. Prefer an explicit `socket.unit` field (set by the dynamic
          //      output deriver, e.g. RasterComponent → "t/ha").
          //   2. Otherwise strip a literal `[[unit]]` marker out of the
          //      primary / secondary text — mirrors the canvas socket
          //      title parser in `node_component.tsx` ~L298.
          const parsedPrimary = parseUnitMarker(rawPrimary)
          const parsedSecondary = rawSecondary ? parseUnitMarker(rawSecondary) : undefined
          const unit = socket.unit ?? parsedPrimary.unit ?? parsedSecondary?.unit
          const density =
            socket.unit !== undefined
              ? !!socket.unitDensity || socket.unit.includes("/")
              : parsedPrimary.density ?? parsedSecondary?.density
          const primary = parsedPrimary.text
          const secondary = parsedSecondary?.text
          const typeLabel = SOCKET_COLOR_LABELS[socket.color]
          return (
            <li key={`${socket.name}-${idx}`} className="model-editor-toolbox-tool-socket">
              <span
                className={`model-editor-toolbox-tool-socket-dot model-editor-toolbox-tool-socket-dot--${socket.color}`}
                aria-hidden
                title={typeLabel}
              />
              <div className="model-editor-toolbox-tool-socket-body">
                <div className="model-editor-toolbox-tool-socket-line">
                  <span className="model-editor-toolbox-tool-socket-name">{primary}</span>
                  {unit && (
                    <span
                      className={`model-editor-toolbox-tool-socket-unit${density ? " model-editor-toolbox-tool-socket-unit--density" : ""}`}
                      title={`Units: ${unit}`}
                    >
                      {unit}
                    </span>
                  )}
                  {socket.multi && (
                    <span
                      className="model-editor-toolbox-tool-socket-multi"
                      title="Accepts multiple connections"
                    >
                      ×n
                    </span>
                  )}
                  <span
                    className={`model-editor-toolbox-tool-socket-type model-editor-toolbox-tool-socket-type--${socket.color}`}
                  >
                    {typeLabel}
                  </span>
                </div>
                {secondary && (
                  <span className="model-editor-toolbox-tool-socket-id">{secondary}</span>
                )}
              </div>
            </li>
          )
        })}
      </ul>
    </div>
  )
}

function NoteQuickAddTool() {
  return (
    <button
      type="button"
      className="model-editor-toolbox-tool model-editor-toolbox-tool--note"
      title="Drag onto the canvas to add a sticky note"
      draggable
      onDragStart={event => {
        if (!event.dataTransfer) return
        event.dataTransfer.effectAllowed = "copy"
        event.dataTransfer.setData("application/x-landscapes-note", "1")
        event.dataTransfer.setData("text/plain", "Sticky note")
        setupOpaqueDragPreview(event)
      }}
      onDragEnd={clearDraggingState}
    >
      <span
        className="model-editor-toolbox-tool-icon model-editor-toolbox-tool-icon--note"
        aria-hidden
      >
        <i className="fas fa-note-sticky" />
      </span>
      <span className="model-editor-toolbox-tool-text">
        <span className="model-editor-toolbox-tool-name">Note</span>
        <span className="model-editor-toolbox-tool-desc">
          Drag onto canvas to drop a sticky note
        </span>
      </span>
      <i className="fas fa-grip-vertical model-editor-toolbox-tool-chevron" aria-hidden />
    </button>
  )
}
